import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode, type ButtonHTMLAttributes } from 'react';
import { I } from './icons';
import { initials } from '../format';

export function Button({ variant, size, loading, className, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'accent' | 'danger' | 'ghost'; size?: 'sm'; loading?: boolean }) {
  return <button {...rest} className={['btn', variant, size, loading ? 'loading' : '', className].filter(Boolean).join(' ')} disabled={rest.disabled || loading} aria-busy={loading || undefined}>{children}</button>;
}

export function Badge({ tone = 'neutral', children, title }: { tone?: 'neutral' | 'brand' | 'accent' | 'success' | 'warning' | 'danger' | 'info'; children: ReactNode; title?: string }) {
  return <span className={`badge ${tone}`} title={title}>{children}</span>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: ReactNode; error?: string | null; children: (id: string) => ReactNode }) {
  const id = useId();
  return <label className="field" htmlFor={id}><span className="label">{label}</span>{children(id)}{error ? <span className="err" role="alert">{error}</span> : hint ? <span className="hint">{hint}</span> : null}</label>;
}

export function TextField({ label, hint, value, onChange, placeholder, type = 'text', disabled, error, autoFocus }: { label: string; hint?: ReactNode; value: string | number | null | undefined; onChange: (v: string) => void; placeholder?: string; type?: string; disabled?: boolean; error?: string | null; autoFocus?: boolean }) {
  return <Field label={label} hint={hint} error={error}>{(id) => <input id={id} className="input" type={type} value={value ?? ''} placeholder={placeholder} disabled={disabled} autoFocus={autoFocus} aria-invalid={error ? true : undefined} onChange={(e) => onChange(e.target.value)} />}</Field>;
}

export function TextAreaField({ label, hint, value, onChange, rows = 4, placeholder, disabled }: { label: string; hint?: ReactNode; value: string | null | undefined; onChange: (v: string) => void; rows?: number; placeholder?: string; disabled?: boolean }) {
  return <Field label={label} hint={hint}>{(id) => <textarea id={id} className="textarea" rows={rows} value={value ?? ''} placeholder={placeholder} disabled={disabled} onChange={(e) => onChange(e.target.value)} />}</Field>;
}

export function SelectField({ label, hint, value, onChange, options, disabled }: { label: string; hint?: ReactNode; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; disabled?: boolean }) {
  return <Field label={label} hint={hint}>{(id) => <select id={id} className="select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>}</Field>;
}

export function Toggle({ label, checked, onChange, disabled }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return <label className="toggle"><input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} /><span className="track" aria-hidden="true" />{label}</label>;
}

export function ChipsField({ label, hint, values, onChange, placeholder }: { label: string; hint?: ReactNode; values: string[]; onChange: (v: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  const add = () => { const v = draft.trim(); if (v && !values.includes(v)) onChange([...values, v]); setDraft(''); };
  return (
    <Field label={label} hint={hint}>{(id) => (
      <div className="chips">
        {values.map((v) => <span className="chip" key={v}>{v}<button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(values.filter((x) => x !== v))}>×</button></span>)}
        <input id={id} value={draft} placeholder={values.length ? '' : placeholder} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1)); }} onBlur={add} />
      </div>
    )}</Field>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode }[]; value: T; onChange: (v: T) => void }) {
  return <div className="tabs" role="tablist">{tabs.map((t) => <button key={t.id} role="tab" className="tab" aria-selected={value === t.id} onClick={() => onChange(t.id)}>{t.label}</button>)}</div>;
}

export function Empty({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return <div className="empty"><h3>{title}</h3>{children && <p className="small">{children}</p>}{action}</div>;
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <div className="note error" role="alert"><span style={{ flex: 1 }}>{message}</span>{onRetry && <Button size="sm" onClick={onRetry}>Try again</Button>}</div>;
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return <div className="stack" aria-busy="true" aria-label="Loading">{Array.from({ length: rows }, (_, i) => <div key={i} className="skeleton" style={{ height: 52, opacity: 1 - i * 0.2 }} />)}</div>;
}

export function Avatar({ name }: { name: string | null | undefined }) { return <span className="avatar" aria-hidden="true">{initials(name)}</span>; }

export function PageHead({ title, sub, actions }: { title: string; sub?: ReactNode; actions?: ReactNode }) {
  return <div className="page-head"><div><h1>{title}</h1>{sub && <p>{sub}</p>}</div>{actions && <div className="row">{actions}</div>}</div>;
}

export function Drawer({ title, onClose, children, footer }: { title: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); prev?.focus(); };
  }, [onClose]);
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true" ref={ref}>
        <div className="drawer-head"><h2 style={{ flex: 1 }}>{title}</h2><Button variant="ghost" onClick={onClose} aria-label="Close"><I.X /></Button></div>
        <div className="drawer-body">{children}</div>
        {footer && <div className="drawer-foot">{footer}</div>}
      </div>
    </>
  );
}

/** Signature element: a ruler that shows how much of Meta's 24-hour reply window is left. */
export function WindowMeter({ expiresAt, now, mini }: { expiresAt: string | null | undefined; now: number; mini?: boolean }) {
  if (!expiresAt) return null;
  const msLeft = new Date(expiresAt).getTime() - now;
  const hours = Math.max(0, msLeft / 3600000);
  const on = Math.ceil(Math.min(24, hours));
  const closed = msLeft <= 0;
  const low = !closed && hours < 2;
  const label = closed ? 'Reply window closed' : hours < 1 ? `${Math.max(1, Math.round(msLeft / 60000))}m left to reply` : `${Math.floor(hours)}h left to reply`;
  return (
    <div className={`meter ${mini ? 'mini' : ''} ${low ? 'low' : ''} ${closed ? 'closed' : ''}`} role="meter" aria-valuemin={0} aria-valuemax={24} aria-valuenow={Math.round(hours)} aria-label={label} title={label}>
      <div className="meter-ticks">{Array.from({ length: 24 }, (_, i) => <i key={i} className={i < on ? 'on' : ''} />)}</div>
      {!mini && <span className="meter-label">{label}</span>}
    </div>
  );
}

type Toast = { id: number; text: string; tone?: 'error' };
const ToastCtx = createContext<(text: string, tone?: 'error') => void>(() => undefined);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone?: 'error') => {
    const id = Date.now() + Math.random();
    setItems((x) => [...x, { id, text, tone }]);
    setTimeout(() => setItems((x) => x.filter((t) => t.id !== id)), tone === 'error' ? 7000 : 3500);
  }, []);
  return <ToastCtx.Provider value={push}>{children}<div className="toasts" role="status" aria-live="polite">{items.map((t) => <div key={t.id} className={`toast ${t.tone ?? ''}`}>{t.tone === 'error' ? <I.X /> : <I.Check />}{t.text}</div>)}</div></ToastCtx.Provider>;
}
export const useToast = () => useContext(ToastCtx);

/** Wraps an async action with loading state and toast feedback. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const run = useCallback(async <T,>(key: string, fn: () => Promise<T>, success?: string): Promise<T | undefined> => {
    setBusy(key);
    try { const r = await fn(); if (success) toast(success); return r; }
    catch (e: any) { toast(e?.message ?? 'That did not work. Try again.', 'error'); return undefined; }
    finally { setBusy(null); }
  }, [toast]);
  return { busy, run };
}
